import { useEffect, useMemo, useState } from 'react';
import { FiEdit2, FiExternalLink, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { deleteProject, fetchAdminProjects } from '../../api/admin';
import styles from './Projects.module.css';

const readData = (response) => {
  if (response.data?.code !== 200) throw new Error(response.data?.message || 'Request failed.');
  return response.data.data;
};

const ProjectManager = () => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const filtered = useMemo(() => items.filter((project) =>
    `${project.name} ${project.summary} ${project.slug}`.toLowerCase().includes(query.toLowerCase())
  ), [items, query]);

  useEffect(() => {
    let active = true;
    fetchAdminProjects()
      .then((response) => { if (active) setItems(readData(response) || []); })
      .catch((requestError) => { if (active) setError(requestError.message || 'Could not load projects.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const removeProject = async (project) => {
    if (!window.confirm(`Delete “${project.name}”? This cannot be undone.`)) return;
    setError('');
    try {
      const response = await deleteProject(project.id);
      readData(response);
      setItems((current) => current.filter((item) => item.id !== project.id));
    } catch (requestError) {
      setError(requestError.message || 'Could not delete the project.');
    }
  };

  return (
    <div>
      <header className={styles.pageHeader}>
        <div>
          <p>PORTFOLIO</p>
          <h1>Projects</h1>
          <span>Maintain project summaries, architecture and media.</span>
        </div>
        <Link to="/admin/projects/new" className={styles.primaryButton}><FiPlus /> New project</Link>
      </header>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.toolbar}>
        <label className={styles.search}><FiSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search projects" /></label>
        <span>{filtered.length} item{filtered.length === 1 ? '' : 's'}</span>
      </div>

      <div className={styles.projectList}>
        {filtered.map((project, index) => (
          <article className={styles.projectRow} key={project.id || project.slug}>
            <div className={styles.projectIndex}>{String(index + 1).padStart(2, '0')}</div>
            <div className={styles.projectCopy}>
              <div><h2>{project.name}</h2><span className={project.status === 'DRAFT' ? styles.draft : styles.published}>{project.status}</span></div>
              <p>{project.summary}</p>
              <small>/{project.slug} · {project.year || 'No year'}</small>
            </div>
            <div className={styles.rowActions}>
              {project.status === 'PUBLISHED' && <a href={`/projects/${project.slug}`} target="_blank" rel="noreferrer" aria-label={`View ${project.name}`}><FiExternalLink /></a>}
              <Link to={`/admin/projects/${project.slug}/edit`} aria-label={`Edit ${project.name}`}><FiEdit2 /></Link>
              <button type="button" onClick={() => removeProject(project)} aria-label={`Delete ${project.name}`}><FiTrash2 /></button>
            </div>
          </article>
        ))}
        {!loading && !filtered.length && <div className={styles.emptyState}>No projects match this search.</div>}
        {loading && <div className={styles.emptyState}>Loading projects…</div>}
      </div>
    </div>
  );
};

export default ProjectManager;
